import React, { useContext, useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { FirstRunChecklist } from "./FirstRunChecklist";
import HomeVisitorFunnel from "./HomeVisitorFunnel";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { 
  LayoutDashboard, 
  Calendar, 
  TrendingUp, 
  CheckSquare, 
  BarChart3, 
  Users, 
  Phone, 
  Camera,
  Settings,
  Building2,
  UserCog,
  List,
  Sparkles,
  CreditCard,
  ArrowLeft,
  AlertTriangle,
  Briefcase,
  ShieldCheck,
  Building,
  Palette,
  LifeBuoy,
  Receipt,
  ClipboardCheck
} from "lucide-react";

export default function Home() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const canUsePhotos = user?.role === "admin" || user?.role === "company_owner" || user?.role === "site_manager";
  const [notesCount, setNotesCount] = useState(0);
  useEffect(() => {
    if (!canUsePhotos) return;
    const token = localStorage.getItem("token");
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/fitoff-notes-count`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setNotesCount(r.data?.count || 0)).catch(() => {});
  }, [canUsePhotos]);

  const mainMenuItems = [
    {
      title: "Site",
      description: "Daily operations and management",
      icon: Building2,
      color: "from-blue-500 to-blue-600",
      show: true,
      hasSubmenu: true,
      submenuItems: [
        {
          title: "Dashboard",
          description: "Daily site entries and activities",
          icon: LayoutDashboard,
          path: "/dashboard",
          color: "from-blue-500 to-blue-600"
        },
        {
          title: "Calendar",
          description: "View scheduled activities",
          icon: Calendar,
          path: "/calendar",
          color: "from-green-500 to-green-600"
        },
        {
          title: "Planner",
          description: "Look forward planning",
          icon: TrendingUp,
          path: "/planner",
          color: "from-purple-500 to-purple-600"
        },
        {
          title: "Tasks",
          description: "Manage tasks and action items",
          icon: CheckSquare,
          path: "/tasks",
          color: "from-amber-500 to-amber-600",
          show: user?.role !== "view_only"
        },
        {
          title: "Expenses",
          description: "Out-of-pocket reimbursement claims",
          icon: Receipt,
          path: "/expenses",
          color: "from-emerald-500 to-teal-600",
          show: !["view_only", "supervisor"].includes(user?.role)
        },
        {
          title: "Reports",
          description: "Analytics and insights",
          icon: BarChart3,
          path: "/reports",
          color: "from-orange-500 to-orange-600"
        },
        {
          title: "Contractor View",
          description: "Track contractor attendance",
          icon: Users,
          path: "/contractor-view",
          color: "from-cyan-500 to-cyan-600"
        },
        {
          title: "Phone Records",
          description: "Telephone discussions log",
          icon: Phone,
          path: "/phone-records",
          color: "from-pink-500 to-pink-600"
        },
        {
          title: "Site Photos",
          description: "Site photo record & client updates",
          icon: Camera,
          path: "/site-photos",
          color: "from-amber-500 to-orange-600",
          show: canUsePhotos
        }
      ]
    },
    {
      title: "Defect Management",
      description: "Track and manage site defects",
      icon: AlertTriangle,
      path: "/defects",
      color: "from-red-500 to-red-600",
      show: true
    },
    {
      title: "Work In Progress",
      description: "Subcontractor fit-off checklists tracked to close-out",
      icon: ClipboardCheck,
      path: "/work-in-progress",
      color: "from-sky-500 to-teal-600",
      badge: notesCount,
      show: ["admin", "company_owner", "site_manager"].includes(user?.role)
    },
    {
      title: "Client Management",
      description: "Manage client information",
      icon: Briefcase,
      path: "/clients",
      color: "from-indigo-500 to-indigo-600",
      show: true
    },
    {
      title: "OH & S",
      description: "Occupational health and safety",
      icon: ShieldCheck,
      path: "/ohs",
      color: "from-emerald-500 to-emerald-600",
      show: true
    },
    {
      title: "Head-Office Overview",
      description: "Company-wide totals and what needs attention",
      icon: LayoutDashboard,
      color: "from-slate-700 to-slate-900",
      show: user?.role === "admin" || user?.role === "company_owner",
      path: "/overview",
    },
    {
      title: "Controls",
      description: "Site and user management",
      icon: Settings,
      color: "from-slate-500 to-slate-600",
      show: user?.role === "admin" || user?.role === "company_owner" || user?.role === "supervisor",
      hasSubmenu: true,
      submenuItems: [
        {
          title: "Sites",
          description: "Manage construction sites",
          icon: Building2,
          path: "/sites",
          color: "from-blue-500 to-blue-600"
        },
        {
          title: "User Control",
          description: "Manage site managers and users",
          icon: UserCog,
          path: "/site-managers",
          color: "from-purple-500 to-purple-600"
        },
        {
          title: "Manage Lists",
          description: "Configure trades and companies",
          icon: List,
          path: "/manage-lists",
          color: "from-green-500 to-green-600"
        },
        {
          title: "Company Profile",
          description: "Your company details and logo",
          icon: Briefcase,
          path: "/company-profile",
          color: "from-indigo-500 to-indigo-600",
          show: user?.role === "admin" || user?.role === "company_owner"
        },
        ...(user?.role === "admin" ? [
          {
            title: "Demo & Trials",
            description: "Review trial requests and notifications",
            icon: Sparkles,
            path: "/admin/demo",
            color: "from-amber-500 to-orange-600"
          },
          {
            title: "Companies",
            description: "Manage tenant companies, owners and lockout",
            icon: Building,
            path: "/admin/companies",
            color: "from-sky-500 to-sky-600"
          },
          {
            title: "Subscriptions",
            description: "Manage plans, billing and subscribers",
            icon: CreditCard,
            path: "/admin/subscriptions",
            color: "from-teal-500 to-teal-600"
          },
          {
            title: "Platform Branding",
            description: "Platform name, logo and support details",
            icon: Palette,
            path: "/admin/branding",
            color: "from-fuchsia-500 to-fuchsia-600"
          }
        ] : [])
      ]
    },
    {
      title: "Contact Support",
      description: "Get help from our support team",
      icon: LifeBuoy,
      path: "/support",
      color: "from-rose-500 to-rose-600",
      show: true
    }
  ];

  const visibleMenuItems = mainMenuItems.filter(item => item.show);

  const handleCardClick = (item) => {
    if (item.hasSubmenu) {
      setActiveSubmenu(item);
    } else {
      navigate(item.path);
    }
  };

  const handleBack = () => {
    setActiveSubmenu(null);
  };

  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Visitor funnel snapshot (Platform Admin only) */}
        {!activeSubmenu && user?.role === "admin" && <HomeVisitorFunnel />}

        {/* First-run setup checklist (Platform Admin & Company Owner) */}
        {!activeSubmenu && <FirstRunChecklist />}

        {/* Welcome Message */}
        {!activeSubmenu && (
          <div className="mb-8">
            <h2 className="heading-primary text-3xl sm:text-4xl mb-2">
              Welcome back, {user?.username}!
            </h2>
            <p className="text-lg text-slate-600">
              What would you like to do today?
            </p>
          </div>
        )}

        {/* Submenu Header with Back Button */}
        {activeSubmenu && (
          <div className="mb-8">
            <Button 
              variant="outline" 
              onClick={handleBack}
              className="mb-4 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Main Menu
            </Button>
            <h2 className="heading-primary text-3xl sm:text-4xl mb-2">
              {activeSubmenu.title}
            </h2>
            <p className="text-lg text-slate-600">
              {activeSubmenu.description}
            </p>
          </div>
        )}

        {/* Menu Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
          {!activeSubmenu ? (
            // Main Menu
            visibleMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Card
                  key={item.title}
                  className="dashboard-card cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-xl relative"
                  onClick={() => handleCardClick(item)}
                >
                  {item.badge > 0 && (
                    <span className="absolute top-2 right-2 min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center shadow" data-testid="wip-notes-badge">
                      {item.badge}
                    </span>
                  )}
                  <CardContent className="p-3">
                    <div className="flex flex-col items-center text-center space-y-2">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${item.color} shadow-lg`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm text-slate-900 mb-0.5">
                          {item.title}
                        </h3>
                        <p className="text-xs text-slate-600">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            // Submenu Items
            activeSubmenu.submenuItems.filter((item) => item.show !== false).map((item) => {
              const Icon = item.icon;
              return (
                <Card
                  key={item.path}
                  className="dashboard-card cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-xl"
                  onClick={() => navigate(item.path)}
                >
                  <CardContent className="p-3">
                    <div className="flex flex-col items-center text-center space-y-2">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${item.color} shadow-lg`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm text-slate-900 mb-0.5">
                          {item.title}
                        </h3>
                        <p className="text-xs text-slate-600">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
